/**
 * Parent record identity parsed from the action plan `sourceDesc` (e.g.
 * `BOS-1301`, `UNC-166`, `DFT-231`). The prefix selects which revamp module
 * modal to open; the number is the parent record id used to open it.
 */
export interface ActionPlanSourceRegistry {
  prefix: string;
  id: number;
}

const SOURCE_REGISTRY_PATTERN = /^([A-Za-z]+)-(\d+)/;

/**
 * Extracts `{ prefix, id }` from a `sourceDesc` like `DFT-231`. Returns `null`
 * when the value is empty or does not match the `<PREFIX>-<number>` shape.
 */
export function parseActionPlanSourceRegistry(sourceDesc?: string | null): ActionPlanSourceRegistry | null {
  if (!sourceDesc) return null;

  const match = SOURCE_REGISTRY_PATTERN.exec(sourceDesc.trim());
  if (!match) return null;

  const id = Number(match[2]);
  if (!Number.isFinite(id) || id <= 0) return null;

  return { prefix: match[1].toUpperCase(), id };
}

/**
 * True when `sourceDesc` carries only the draft placeholder generated by an
 * integration — a bare numeric reference with no module tag (e.g.
 * `544230101`). Those ids are meaningless to users and must render as `-`
 * until the definitive tagged reference exists (AB#68945).
 */
export function isDraftSourceRef(sourceDesc?: string | null): boolean {
  const normalized = String(sourceDesc ?? '').trim();
  return normalized.length > 0 && /^\d+$/.test(normalized);
}
